import { createSelector } from 'reselect';
import get from 'lodash/get';
import {
  selectedGoalSelector,
  nullableGoalViewStateSelector,
  goalId as goalIdSelector,
} from '../../FinancialPlanGoalDropdown/FinancialPlanGoalDropdownSelector';
import { goals as goalsSelector } from '../FinancialPlanSelector';
import { LIFE, DISABILITY, LONG_TERM_CARE, INSURANCE_GOAL_TYPE } from './InsuranceConstants';
import { NEEDS_ANALYSIS, INCOME_REPLACEMENT } from './Life/LifeConstants';
import { isNonEmptyString } from '../../../js/app/react/validators';
import { ASSUMPTIONS } from '../Explanations/ExplanationsConst';

export const insuranceGoal = (state, props) =>
  goalsSelector(state, props).find(goal => goal.GoalType === INSURANCE_GOAL_TYPE);

export const nullableSelectedInsuranceTypeSelector = createSelector(
  nullableGoalViewStateSelector,
  goalIdSelector,
  goalsSelector,
  (nullableGoalViewState, goalId, goals) => {
    const nullableSelectedGoal = goals.find(goal => goal.GoalId === goalId);

    if (!nullableSelectedGoal || nullableSelectedGoal.GoalType !== INSURANCE_GOAL_TYPE) {
      return null;
    }

    if (!nullableGoalViewState || !nullableGoalViewState.selectedSection) {
      return null;
    }
    return nullableGoalViewState.selectedSection;
  },
);

export const selectedInsuranceTypeOrDefaultSelector = createSelector(
  nullableSelectedInsuranceTypeSelector,
  nullableSelectedInsuranceType => nullableSelectedInsuranceType || LIFE,
);

export const subgoalOrDefaultSelector = createSelector(
  selectedGoalSelector,
  selectedInsuranceTypeOrDefaultSelector,
  (nullableSelectedGoal, selectedInsuranceTypeOrDefault) => {
    const DEFAULT = {};
    if (nullableSelectedGoal === null || nullableSelectedGoal.GoalType !== INSURANCE_GOAL_TYPE) {
      return DEFAULT;
    }

    switch (selectedInsuranceTypeOrDefault) {
      case LIFE:
        return nullableSelectedGoal.LifeInsurance;
      case DISABILITY:
        return nullableSelectedGoal.DisabilityInsurance;
      case LONG_TERM_CARE:
        return nullableSelectedGoal.LongTermCareInsurance;
      default:
        return DEFAULT;
    }
  },
);

export const selectedOwnerIndexSelector = createSelector(
  nullableGoalViewStateSelector,
  nullableGoalViewState => (nullableGoalViewState || {}).ownerIndex || 0,
);

export const ownersOrDefaultSelector = createSelector(
  subgoalOrDefaultSelector,
  ({ PrincipalName, SpouseName }) => {
    const options = [];

    if (isNonEmptyString(PrincipalName)) {
      options.push(PrincipalName);
    }

    if (isNonEmptyString(SpouseName)) {
      options.push(SpouseName);
    }
    return options;
  },
);

export const selectedOwnerNameSelector = createSelector(
  selectedOwnerIndexSelector,
  ownersOrDefaultSelector,
  (index, owners) => {
    if (index < 0 || index >= owners.length) {
      const { ADV } = window;
      // should throw but we don't have an error
      // consumption mechanism so we default to the generic error string
      return ADV.settings.MsgLib.generic_error;
    }
    return `${owners[index]}`;
  },
);

export const selectedOwnerLabelSelector = createSelector(
  selectedOwnerIndexSelector,
  ownersOrDefaultSelector,
  (index, owners) => {
    if (index < 0 || index >= owners.length) {
      const { ADV } = window;
      // should throw but we don't have an error
      // consumption mechanism so we default to the generic error string
      return ADV.settings.MsgLib.generic_error;
    }
    return `${owners[index]}'s Insurance`;
  },
);

export const selectedTabIndexOrDefaultSelector = createSelector(
  nullableGoalViewStateSelector,
  selectedInsuranceTypeOrDefaultSelector,
  (nullableGoalViewState, selectedInsuranceTypeOrDefault) => {
    const DEFAULT = 0;
    if (nullableGoalViewState === null) {
      return DEFAULT;
    }
    return get(
      nullableGoalViewState,
      `subgoals.${selectedInsuranceTypeOrDefault}.tabIndex`,
      DEFAULT,
    );
  },
);

export const tabsOrDefaultSelector = createSelector(
  selectedGoalSelector,
  selectedInsuranceTypeOrDefaultSelector,
  (nullableSelectedGoal, selectedInsuranceTypeOrDefault) => {
    const DEFAULT = [];
    if (nullableSelectedGoal === null || nullableSelectedGoal.GoalType !== INSURANCE_GOAL_TYPE) {
      return DEFAULT;
    }
    switch (selectedInsuranceTypeOrDefault) {
      case LIFE:
        return [NEEDS_ANALYSIS, INCOME_REPLACEMENT];
      default:
        return DEFAULT;
    }
  },
);

export const selectedTabNameOrDefaultSelector = createSelector(
  tabsOrDefaultSelector,
  selectedTabIndexOrDefaultSelector,
  (tabs, index) => {
    const DEFAULT = '';
    if (index < 0 || index >= tabs.length) {
      return DEFAULT;
    }
    return `${tabs[index]}`;
  },
);

export const insuranceSelectedSectionSelector = createSelector(
  selectedInsuranceTypeOrDefaultSelector,
  selectedOwnerNameSelector,
  selectedTabNameOrDefaultSelector,
  (selectedInsuranceTypeOrDefault, selectedOwnerName, selectedTabNameOrDefault) => {
    if (selectedInsuranceTypeOrDefault === ASSUMPTIONS) {
      return ASSUMPTIONS;
    }
    return `${selectedInsuranceTypeOrDefault}.${selectedOwnerName}.${selectedTabNameOrDefault}`;
  },
);
